import { gql, useApolloClient } from "@apollo/client";
import { CardContainer, ModalOpenButton } from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button, IconButton, List, Pagination } from "@mui/material";
import React from "react";
import { SHORT_LIST_RESULTS_PER_PAGE } from "../../../constants.js";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import {
  CrmCompanyBuildingsBox__BuildingFragment,
  useLinkBuildingToCrmOrganisationMutation,
} from "./CrmCompanyBuildingsBox.generated.js";
import { useTranslate } from "@tolgee/react";
import { BuildingIcon } from "../../buildings/BuildingIcon.js";
import BuildingRow from "../../buildings/components/BuildingRow.js";
import { BuildingCreateModal } from "../../buildings/modals/BuildingCreateModal.js";
import { BuildingSelectModal } from "../../buildings/modals/BuildingSelectModal.js";
import { Link } from "react-router-dom";
import { getUrlSearchParamsByParamsName } from "../../../commons/hooks/useStateWithUrlParams.js";

interface Props {
  crmCompanyId: string;
  buildings: CrmCompanyBuildingsBox__BuildingFragment[];
  isLoading: boolean;
}

export const CrmCompanyBuildingsBox = ({
  crmCompanyId,
  buildings,
  isLoading,
}: Props) => {
  const { t } = useTranslate(["Projects", "Global"]);

  const [page, setPage] = React.useState(1);
  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const client = useApolloClient();
  const [linkBuildingToCrmOrganisation, query] =
    useLinkBuildingToCrmOrganisationMutation({
      client,
      refetchQueries: [namedOperations.Query.CrmCompanyProfile],
    });

  const totalCount = buildings.length;

  const offset = (page - 1) * SHORT_LIST_RESULTS_PER_PAGE;
  const limit = SHORT_LIST_RESULTS_PER_PAGE;
  const pageCount = Math.ceil(totalCount / SHORT_LIST_RESULTS_PER_PAGE);

  return (
    <CardContainer
      Icon={<BuildingIcon />}
      title={t("Buildings")}
      itemCount={totalCount}
      ActionButton={
        <ModalOpenButton
          Modal={BuildingSelectModal}
          modalProps={{
            crmOrganisation: {
              id: crmCompanyId,
              mode: "exclude",
            },
            onCreateRefetchQueries: [namedOperations.Query.CrmCompanyProfile],
            handleComplete: async (
              building,
              buildingRootItemId,
              handleClose
            ) => {
              await linkBuildingToCrmOrganisation({
                variables: {
                  buildingId: building.id,
                  crmOrganisationId: crmCompanyId,
                },
              });
              handleClose();
            },
            closeAfterCreate: true,
            hidePlacementSelect: true,
          }}
        >
          <IconButton size="small" color="primary">
            <AddIcon />
          </IconButton>
        </ModalOpenButton>
      }
      isLoading={isLoading}
    >
      <List disablePadding>
        {buildings
          .slice(offset, offset + limit)
          .map((building, index, array) => (
            <BuildingRow
              key={building.id}
              building={building}
              divider={index < array.length - 1}
            />
          ))}
      </List>
      {buildings.length > 0 && (
        <Box>
          <Button
            component={Link}
            to={{
              pathname: `/buildings`,
              search: getUrlSearchParamsByParamsName(
                { crmOrganisationId: crmCompanyId },
                "filters"
              ).toString(),
            }}
            color="secondary"
            variant="text"
            fullWidth
          >
            {t("See all", { ns: "Global" })}
          </Button>
        </Box>
      )}
    </CardContainer>
  );
};
